import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  FlatList,
  Image,
  Keyboard,
  KeyboardAvoidingView,
  ListRenderItemInfo,
  Modal,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useSelector} from 'react-redux';

import botAvatar from '/asset/chat/bot-avatar.png';
import Avatar from '/component/base/Avatar';
import Icon from '/component/base/Icon';
import {withTheme} from '/provider/ThemeProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {
  fetchDeploymentConversationHistory,
  fetchLastDataRefreshStatus,
  setDeploymentConversationFeedback,
} from '/redux/action/abacus.action';
import {avatarUrlById, fullNameById} from '/redux/selector/coach.selector';
import {userId as selectUserId} from '/redux/selector/currentUser.selector';
import {selectChatQuickSuggestions} from '/redux/selector/remoteConfig.selector';
import {black, dodgerBlue, white} from '/theme/color/on.color';
import {AsyncStorage} from '/util/async-storage';
import {copyToClipboard} from '/util/clipboard.util';
import {withHOC} from '/util/hoc.util';
import {Logger} from '/util/splunk/logger.util';

import ChatHistoryPanel from './components/ChatHistoryPanel';
import ChatInfoSheet from './components/ChatInfoSheet';
import ChatMessagesPanel from './components/ChatMessagesPanel';
import {
  DEFAULT_CHAT_TOPIC,
  INTERIM_MESSAGE_DELAYS_MS,
  LAST_SYNC_FETCH_COOLDOWN_MS,
  QUICK_SUGGESTIONS,
} from './constants/ChatModal.constants';
import {CHAT_TEXT} from './constants/chatText.constants';
import {
  ChatMessageListItem,
  ChatModalProps,
  ConversationThread,
  HistoryApiMessage,
  Message,
  PersistedChatHistory,
} from './types/ChatModal.types';
import {
  buildConversationTitle,
  buildSupportReference,
  createChatHistoryStorageKey,
  createEmptyConversation,
  createMessageId,
  formatSupportReferenceLabel,
  getContextualLoadingMessages,
  getDeploymentConversationIdCandidates,
  isDraftConversation,
} from './util/chatConversation.util';
import {
  buildHistoryConversationSections,
  formatConversationDateTag,
  formatLastSyncedTimestamp,
  formatMessageTimestamp,
  isSameCalendarDay,
  parseDate,
} from './util/chatDate.util';
import {
  createFallbackConversationState,
  isUserHistoryRole,
  normalizeHistoryTimestamp,
  resolveHistoryMessageRole,
  resolveHistoryMessageText,
  sanitizePersistedHistory,
} from './util/chatHistory.util';
import {parseInlineTextSegments} from './util/chatInlineText.util';
import {stripSensitiveChatText} from './util/chatSanitization.util';
import {
  getTableColumnWidths,
  TABLE_MIN_COLUMN_WIDTH,
} from './util/chatTable.util';
import {
  ChatMessageBlock,
  parseChatMessageContent,
} from './chatMessageContent.util';
import {createDynamicStyles, styles} from './ChatModal.style';

const renderInlineText = (value: string, keyPrefix: string) =>
  parseInlineTextSegments(value).map((segment, segmentIndex) => {
    if (!segment.isBold) {
      return segment.text;
    }

    return (
      <Text
        key={`${keyPrefix}-bold-${segmentIndex}`}
        style={styles.inlineBoldText}>
        {segment.text}
      </Text>
    );
  });

const normalizeAgentStatusToken = (value: string): string =>
  value
    .trim()
    .replace(/^['"]+|['"]+$/g, '')
    .replace(/^[[({\s]+/, '')
    .replace(/[\])}\s]+$/, '')
    .trim()
    .toLowerCase();

const AGENT_STATUS_WORDS = new Set([
  'skipped',
  'pending',
  'processing',
  'queued',
  'cancelled',
  'canceled',
  'aborted',
  'error',
  'failed',
  'timeout',
  'null',
  'undefined',
]);

const HISTORY_PAGE_SIZE = 10;
const HISTORY_LOAD_MORE_DELAY_MS = 320;
const HISTORY_OPEN_SHIMMER_MS = 320;

const sanitizeAssistantReply = (value: unknown): string => {
  if (typeof value !== 'string') {
    return '';
  }

  const trimmed = stripSensitiveChatText(value).trim();
  if (!trimmed) {
    return '';
  }

  if (AGENT_STATUS_WORDS.has(normalizeAgentStatusToken(trimmed))) {
    return '';
  }

  return trimmed;
};

const parseIsApprovedValue = (value: unknown): boolean | null | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
    return undefined;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
    if (
      normalized === '' ||
      normalized === 'null' ||
      normalized === 'undefined'
    ) {
      return null;
    }
  }

  return undefined;
};

const resolveIsApprovedFromResponse = (
  response:
    | {
        isApproved?: boolean | null;
        segments?: Array<Record<string, any>> | null;
      }
    | null
    | undefined,
): boolean | null | undefined => {
  const directApproval = parseIsApprovedValue(response?.isApproved);
  if (directApproval !== undefined) {
    return directApproval;
  }

  const segments = response?.segments;
  if (!Array.isArray(segments)) {
    return undefined;
  }

  for (const segment of segments) {
    if (!segment || typeof segment !== 'object') {
      continue;
    }

    if (segment.id !== 'is_approved') {
      continue;
    }

    const candidates = [
      segment.segment,
      segment.text,
      segment.value,
      segment.data?.value,
      segment.data?.text,
    ];
    for (const candidate of candidates) {
      const parsed = parseIsApprovedValue(candidate);
      if (parsed !== undefined) {
        return parsed;
      }
    }

    return null;
  }

  return undefined;
};

const parseMessageIndexValue = (value: unknown): number | null | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
      return null;
    }

    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return Math.trunc(parsed);
    }
  }

  return undefined;
};

const resolveMessageIndexFromResponse = (
  response:
    | {
        messageIndex?: number | string | null;
        message_index?: number | string | null;
        result?: {
          messageIndex?: number | string | null;
          message_index?: number | string | null;
        } | null;
        segments?: Array<Record<string, any>> | null;
      }
    | null
    | undefined,
): number | null | undefined => {
  const directCandidates = [
    response?.messageIndex,
    response?.message_index,
    response?.result?.messageIndex,
    response?.result?.message_index,
  ];

  for (const candidate of directCandidates) {
    const parsed = parseMessageIndexValue(candidate);
    if (parsed !== undefined) {
      return parsed;
    }
  }

  const segments = response?.segments;
  if (!Array.isArray(segments)) {
    return undefined;
  }

  for (const segment of segments) {
    if (!segment || typeof segment !== 'object') {
      continue;
    }

    const nestedSegment =
      segment.segment && typeof segment.segment === 'object'
        ? segment.segment
        : null;
    const candidates = [
      segment.messageIndex,
      segment.message_index,
      segment.value,
      segment.segment,
      segment.data?.value,
      segment.data?.messageIndex,
      segment.data?.message_index,
      nestedSegment?.messageIndex,
      nestedSegment?.message_index,
      nestedSegment?.value,
    ];

    for (const candidate of candidates) {
      const parsed = parseMessageIndexValue(candidate);
      if (parsed !== undefined) {
        return parsed;
      }
    }
  }

  return undefined;
};

const normalizeFeedbackComparableText = (value: string): string =>
  stripSensitiveChatText(value).trim().replace(/\s+/g, ' ').toLowerCase();

const ChatModal: React.FC<ChatModalProps> = ({
  visible,
  onClose,
  showMessageTimestamp = true,
  onSendMessage,
  getTheme,
  ts,
}) => {
  const initialConversationRef = useRef<ConversationThread>(
    createEmptyConversation(),
  );
  const [conversations, setConversations] = useState<ConversationThread[]>([
    initialConversationRef.current,
  ]);
  const [activeConversationId, setActiveConversationId] = useState<string>(
    initialConversationRef.current.id,
  );
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [isHistoryInitializing, setIsHistoryInitializing] = useState(true);
  const [isHistoryLoadingMore, setIsHistoryLoadingMore] = useState(false);
  const [unloadedHistoryCount, setUnloadedHistoryCount] = useState(0);
  const [editingConversationId, setEditingConversationId] = useState<
    string | null
  >(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [inputResetKey, setInputResetKey] = useState(0);
  const [loadingByConversationId, setLoadingByConversationId] = useState<
    Record<string, boolean>
  >({});
  const [fetchingHistoryByConversationId, setFetchingHistoryByConversationId] =
    useState<Record<string, boolean>>({});
  const [
    loadingStatusMessagesByConversationId,
    setLoadingStatusMessagesByConversationId,
  ] = useState<Record<string, string[]>>({});
  const [isChatInfoVisible, setIsChatInfoVisible] = useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [lastSyncedTime, setLastSyncedTime] = useState<string | null>(null);
  const [streamingTextByMessageId, setStreamingTextByMessageId] = useState<
    Record<string, string>
  >({});
  const messagesListRef = useRef<FlatList<ChatMessageListItem>>(null);
  const textInputRef = useRef<TextInput>(null);
  const pendingScrollTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>(
    [],
  );
  const lastContentSizeScrollAtRef = useRef(0);
  const parsedMessageCacheRef = useRef<
    Record<
      string,
      {
        text: string;
        timestamp: string;
        formattedTime: string;
        sanitizedText: string;
        messageBlocks: ChatMessageBlock[];
      }
    >
  >({});
  const persistHistoryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isLastSyncedLoadingRef = useRef(false);
  const lastSyncedValueRef = useRef<string | null>(null);
  const lastSyncedLastFetchedAtRef = useRef<number | null>(null);
  const wasVisibleRef = useRef(false);
  const hasInitializedOpenStateRef = useRef(false);
  const isHistoryInitializedRef = useRef(false);
  const pendingHistoryLoadTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const historyOpenShimmerTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const insets = useSafeAreaInsets();
  const currentUserId = useSelector(selectUserId);
  const userAvatarUrl = useSelector(state =>
    currentUserId
      ? avatarUrlById(state as any, {coachId: currentUserId})
      : null,
  );
  const userFullName = useSelector(state =>
    currentUserId ? fullNameById(state as any, {coachId: currentUserId}) : null,
  );
  const remoteQuickSuggestions = useSelector(selectChatQuickSuggestions);
  const activeQuickSuggestions =
    remoteQuickSuggestions.length > 0
      ? remoteQuickSuggestions
      : QUICK_SUGGESTIONS;
  const primaryColor =
    getTheme('competency.backgroundColor.color') || dodgerBlue;
  const backgroundColor = getTheme('background.color') || white;
  const textColor = getTheme('text.color') || black;
  const activeConversation = useMemo(
    () =>
      conversations.find(
        conversation => conversation.id === activeConversationId,
      ) || conversations[0],
    [activeConversationId, conversations],
  );
  const messages = useMemo(
    () => activeConversation?.messages || [],
    [activeConversation],
  );
  const suggestions =
    messages.length === 0 && !activeConversation?.hasUsedSuggestion
      ? activeQuickSuggestions.map(s => s.query)
      : activeConversation?.suggestions || [];
  const isActiveConversationLoading = !!(
    activeConversation && loadingByConversationId[activeConversation.id]
  );
  const isActiveConversationFetchingHistory = !!(
    activeConversation && fetchingHistoryByConversationId[activeConversation.id]
  );
  const loadingStatusMessages =
    (activeConversation &&
      loadingStatusMessagesByConversationId[activeConversation.id]) ||
    [];
  const historyConversations = useMemo(
    () =>
      conversations.filter(conversation => !isDraftConversation(conversation)),
    [conversations],
  );
  const hasMoreHistory = unloadedHistoryCount > 0;
  const hasHistoryConversation = useMemo(
    () =>
      historyConversations.some(
        conversation =>
          conversation.conversationId !== null ||
          conversation.externalSessionId !== null ||
          conversation.messages.length > 0,
      ) || unloadedHistoryCount > 0,
    [historyConversations, unloadedHistoryCount],
  );
  const historyConversationSections = useMemo(
    () => buildHistoryConversationSections(historyConversations),
    [historyConversations],
  );

  useEffect(
    () => () => {
      if (persistHistoryTimeoutRef.current) {
        clearTimeout(persistHistoryTimeoutRef.current);
        persistHistoryTimeoutRef.current = null;
      }
      if (pendingHistoryLoadTimeoutRef.current) {
        clearTimeout(pendingHistoryLoadTimeoutRef.current);
        pendingHistoryLoadTimeoutRef.current = null;
      }
      if (historyOpenShimmerTimeoutRef.current) {
        clearTimeout(historyOpenShimmerTimeoutRef.current);
        historyOpenShimmerTimeoutRef.current = null;
      }
    },
    [],
  );
  useEffect(() => {
    let isSubscribed = true;
    isHistoryInitializedRef.current = false;
    setIsHistoryInitializing(true);
    setIsHistoryLoadingMore(false);
    setUnloadedHistoryCount(0);

    if (pendingHistoryLoadTimeoutRef.current) {
      clearTimeout(pendingHistoryLoadTimeoutRef.current);
      pendingHistoryLoadTimeoutRef.current = null;
    }

    const initializeWithFallback = () => {
      const fallback = createFallbackConversationState();
      setConversations(fallback.conversations);
      setActiveConversationId(fallback.activeConversationId);
      setUnloadedHistoryCount(0);
      setIsHistoryLoadingMore(false);
    };

    const loadConversationHistory = async () => {
      if (!currentUserId) {
        if (!isSubscribed) {
          return;
        }

        initializeWithFallback();
        isHistoryInitializedRef.current = true;
        setIsHistoryInitializing(false);
        return;
      }

      try {
        const storageKey = createChatHistoryStorageKey(currentUserId);
        const rawHistory = await AsyncStorage.getItem(storageKey);
        if (!isSubscribed) {
          return;
        }

        if (!rawHistory) {
          initializeWithFallback();
          isHistoryInitializedRef.current = true;
          setIsHistoryInitializing(false);
          return;
        }

        const parsedHistory = sanitizePersistedHistory(JSON.parse(rawHistory));
        if (!parsedHistory) {
          initializeWithFallback();
          isHistoryInitializedRef.current = true;
          setIsHistoryInitializing(false);
          return;
        }

        const parsedConversations = parsedHistory.conversations;
        const draftConversations = parsedConversations.filter(conversation =>
          isDraftConversation(conversation),
        );
        const historyOnlyConversations = parsedConversations.filter(
          conversation => !isDraftConversation(conversation),
        );

        if (
          parsedConversations.length === 0 ||
          historyOnlyConversations.length === 0
        ) {
          initializeWithFallback();
          isHistoryInitializedRef.current = true;
          setIsHistoryInitializing(false);
          return;
        }

        const initialHistoryConversations = historyOnlyConversations.slice(
          0,
          HISTORY_PAGE_SIZE,
        );
        const initialConversations = [
          ...draftConversations,
          ...initialHistoryConversations,
        ];
        const nextUnloadedHistoryCount = Math.max(
          historyOnlyConversations.length - initialHistoryConversations.length,
          0,
        );
        const activeConversationExists = parsedConversations.some(
          conversation =>
            conversation.id === parsedHistory.activeConversationId,
        );
        const nextActiveConversationId = activeConversationExists
          ? initialConversations.some(
              conversation =>
                conversation.id === parsedHistory.activeConversationId,
            )
            ? parsedHistory.activeConversationId
            : initialConversations[0].id
          : initialConversations[0].id;

        setConversations(initialConversations);
        setUnloadedHistoryCount(nextUnloadedHistoryCount);
        setActiveConversationId(nextActiveConversationId);
        setIsHistoryInitializing(false);
      } catch (_error) {
        if (!isSubscribed) {
          return;
        }

        initializeWithFallback();
        setIsHistoryInitializing(false);
      } finally {
        if (isSubscribed) {
          isHistoryInitializedRef.current = true;
        }
      }
    };

    loadConversationHistory();

    return () => {
      isSubscribed = false;
      isHistoryInitializedRef.current = false;
      if (pendingHistoryLoadTimeoutRef.current) {
        clearTimeout(pendingHistoryLoadTimeoutRef.current);
        pendingHistoryLoadTimeoutRef.current = null;
      }
      if (historyOpenShimmerTimeoutRef.current) {
        clearTimeout(historyOpenShimmerTimeoutRef.current);
        historyOpenShimmerTimeoutRef.current = null;
      }
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!currentUserId || !isHistoryInitializedRef.current) {
      return;
    }

    const storageKey = createChatHistoryStorageKey(currentUserId);
    const persistHistory = async () => {
      let conversationsForPersistence = conversations;

      if (unloadedHistoryCount > 0) {
        try {
          const rawHistory = await AsyncStorage.getItem(storageKey);
          const parsedHistory = rawHistory
            ? sanitizePersistedHistory(JSON.parse(rawHistory))
            : null;

          if (parsedHistory) {
            const loadedById = new Map(
              conversations.map(conversation => [
                conversation.id,
                conversation,
              ]),
            );
            const mergedConversations = parsedHistory.conversations.map(
              conversation => loadedById.get(conversation.id) || conversation,
            );
            const existingConversationIds = new Set(
              mergedConversations.map(conversation => conversation.id),
            );
            const additionalConversations = conversations.filter(
              conversation => !existingConversationIds.has(conversation.id),
            );
            conversationsForPersistence = [
              ...additionalConversations,
              ...mergedConversations,
            ];
          }
        } catch (_error) {
          conversationsForPersistence = conversations;
        }
      }

      const payload: PersistedChatHistory = {
        activeConversationId,
        conversations: conversationsForPersistence.map(conversation => ({
          ...conversation,
          messages: [],
        })),
      };

      AsyncStorage.setItem(storageKey, JSON.stringify(payload)).catch(
        () => undefined,
      );
    };

    if (persistHistoryTimeoutRef.current) {
      clearTimeout(persistHistoryTimeoutRef.current);
    }

    persistHistoryTimeoutRef.current = setTimeout(() => {
      persistHistoryTimeoutRef.current = null;
      persistHistory();
    }, 300);

    return () => {
      if (persistHistoryTimeoutRef.current) {
        clearTimeout(persistHistoryTimeoutRef.current);
        persistHistoryTimeoutRef.current = null;
      }
    };
  }, [
    activeConversationId,
    conversations,
    currentUserId,
    unloadedHistoryCount,
  ]);

  const updateConversation = useCallback(
    (
      conversationId: string,
      updater: (conversation: ConversationThread) => ConversationThread,
      moveToTop: boolean = false,
    ) => {
      setConversations(previousConversations => {
        const currentIndex = previousConversations.findIndex(
          conversation => conversation.id === conversationId,
        );
        if (currentIndex < 0) {
          return previousConversations;
        }
        const currentConversation = previousConversations[currentIndex];
        if (!currentConversation) {
          return previousConversations;
        }
        const updatedConversation = updater(currentConversation);
        const nextConversations = [...previousConversations];
        nextConversations[currentIndex] = updatedConversation;

        if (!moveToTop || currentIndex === 0) {
          return nextConversations;
        }

        nextConversations.splice(currentIndex, 1);
        nextConversations.unshift(updatedConversation);
        return nextConversations;
      });
    },
    [],
  );

  const scrollToBottom = useCallback((animated: boolean = true) => {
    requestAnimationFrame(() => {
      messagesListRef.current?.scrollToEnd?.({animated});
    });
  }, []);
  const scrollToTop = useCallback((animated: boolean = false) => {
    requestAnimationFrame(() => {
      messagesListRef.current?.scrollToOffset?.({offset: 0, animated});
    });
  }, []);

  const clearPendingScrollTimeouts = useCallback(() => {
    pendingScrollTimeoutsRef.current.forEach(timeoutId =>
      clearTimeout(timeoutId),
    );
    pendingScrollTimeoutsRef.current = [];
  }, []);

  const scheduleScrollToBottom = useCallback(() => {
    clearPendingScrollTimeouts();
    scrollToBottom(false);

    const timeoutId = setTimeout(() => {
      scrollToBottom(true);
    }, 90);
    pendingScrollTimeoutsRef.current.push(timeoutId);
  }, [clearPendingScrollTimeouts, scrollToBottom]);

  const clearInput = useCallback(() => {
    setInputResetKey(previous => previous + 1);
    requestAnimationFrame(() => {
      textInputRef.current?.clear();
    });
  }, []);

  const refreshLastSynced = useCallback(
    async ({
      force = false,
      lastSuccessfulRunTime = null,
    }: {
      force?: boolean;
      lastSuccessfulRunTime?: string | null;
    } = {}) => {
      if (!visible || isLastSyncedLoadingRef.current) {
        return;
      }

      const normalizedLastSuccessfulRunTime =
        typeof lastSuccessfulRunTime === 'string' &&
        lastSuccessfulRunTime.trim().length > 0
          ? parseDate(lastSuccessfulRunTime.trim())?.toISOString() || null
          : null;

      if (
        normalizedLastSuccessfulRunTime &&
        normalizedLastSuccessfulRunTime !== lastSyncedValueRef.current
      ) {
        lastSyncedValueRef.current = normalizedLastSuccessfulRunTime;
        setLastSyncedTime(normalizedLastSuccessfulRunTime);
      }

      const now = Date.now();
      const lastFetchedAt = lastSyncedLastFetchedAtRef.current;
      if (
        !force &&
        lastFetchedAt !== null &&
        now - lastFetchedAt < LAST_SYNC_FETCH_COOLDOWN_MS
      ) {
        return;
      }

      isLastSyncedLoadingRef.current = true;
      lastSyncedLastFetchedAtRef.current = now;

      try {
        const response = await fetchLastDataRefreshStatus();
        const rawLastSuccessfulRunTime =
          typeof response?.lastSuccessfulRunTime === 'string' &&
          response.lastSuccessfulRunTime.trim().length > 0
            ? response.lastSuccessfulRunTime.trim()
            : null;
        const fetchedNormalizedLastSuccessfulRunTime = rawLastSuccessfulRunTime
          ? parseDate(rawLastSuccessfulRunTime)?.toISOString() || null
          : null;
        const nextValue =
          fetchedNormalizedLastSuccessfulRunTime ||
          lastSyncedValueRef.current ||
          new Date().toISOString();

        lastSyncedValueRef.current = nextValue;
        setLastSyncedTime(nextValue);
      } catch (error) {
        Logger.error(error);
      } finally {
        isLastSyncedLoadingRef.current = false;
      }
    },
    [visible],
  );

  useEffect(() => {
    if (!visible) {
      return;
    }

    refreshLastSynced();
  }, [refreshLastSynced, visible]);

  const handleSuggestionPress = async (suggestion: string) => {
    if (!activeConversation) {
      return;
    }

    updateConversation(activeConversation.id, conversation => ({
      ...conversation,
      hasUsedSuggestion: true,
      suggestions: [],
      updatedAt: new Date().toISOString(),
    }));

    await handleSendText(suggestion);
  };

  const setStreamingMessageText = useCallback(
    (messageId: string, text: string) => {
      setStreamingTextByMessageId(previous => {
        const existing = previous[messageId];
        if (existing === text) {
          return previous;
        }

        return {
          ...previous,
          [messageId]: text,
        };
      });
    },
    [],
  );

  const clearStreamingMessageText = useCallback((messageId: string) => {
    setStreamingTextByMessageId(previous => {
      if (!(messageId in previous)) {
        return previous;
      }

      const next = {...previous};
      delete next[messageId];
      return next;
    });
  }, []);

  const handleSendText = async (rawText: string): Promise<boolean> => {
    const trimmedText = rawText.trim();
    const sanitizedInputText = stripSensitiveChatText(trimmedText).trim();
    const targetConversation = activeConversation;
    if (
      !trimmedText ||
      !sanitizedInputText ||
      !targetConversation ||
      loadingByConversationId[targetConversation.id]
    ) {
      return false;
    }

    const userMessage: Message = {
      id: createMessageId(),
      text: sanitizedInputText,
      isUser: true,
      timestamp: new Date().toISOString(),
    };

    const botMessageId = createMessageId();
    const botMessage: Message = {
      id: botMessageId,
      text: '',
      isUser: false,
      timestamp: new Date().toISOString(),
      isStreaming: true,
    };

    updateConversation(
      targetConversation.id,
      conversation => ({
        ...conversation,
        title:
          conversation.messages.length === 0
            ? buildConversationTitle(sanitizedInputText)
            : conversation.title,
        hasUsedSuggestion: true,
        suggestions: [],
        messages: [...conversation.messages, userMessage, botMessage],
        updatedAt: new Date().toISOString(),
      }),
      true,
    );
    setStreamingMessageText(botMessageId, '');

    clearInput();
    setLoadingByConversationId(previous => ({
      ...previous,
      [targetConversation.id]: true,
    }));
    const fallbackInterimMessages =
      getContextualLoadingMessages(sanitizedInputText);
    setLoadingStatusMessagesByConversationId(previous => ({
      ...previous,
      [targetConversation.id]: [
        fallbackInterimMessages[0] ||
          ts('chat.typing', CHAT_TEXT.typingFallback),
      ],
    }));

    let hasStreamedResponse = false;
    let hasBackendProgress = false;
    let partialUpdateTimeout: ReturnType<typeof setTimeout> | null = null;
    let latestPartialText = '';
    const interimTimers: Array<ReturnType<typeof setTimeout>> = [];
    const clearInterimTimers = () => {
      interimTimers.forEach(timerId => clearTimeout(timerId));
      interimTimers.length = 0;
    };
    const flushPartialUpdate = () => {
      if (!latestPartialText) {
        return;
      }

      updateBotMessage(latestPartialText);
    };
    const schedulePartialUpdate = (nextPartialText: string) => {
      latestPartialText = nextPartialText;

      if (partialUpdateTimeout) {
        return;
      }

      partialUpdateTimeout = setTimeout(() => {
        partialUpdateTimeout = null;
        flushPartialUpdate();
      }, 80);
    };
    const clearPartialUpdateTimeout = () => {
      if (!partialUpdateTimeout) {
        return;
      }

      clearTimeout(partialUpdateTimeout);
      partialUpdateTimeout = null;
    };
    INTERIM_MESSAGE_DELAYS_MS.forEach((delayMs, index) => {
      const timerId = setTimeout(() => {
        if (hasStreamedResponse || hasBackendProgress) {
          return;
        }

        const nextMessage = fallbackInterimMessages[index + 1];
        if (nextMessage) {
          setLoadingStatusMessagesByConversationId(previous => ({
            ...previous,
            [targetConversation.id]: [
              ...(previous[targetConversation.id] || []),
              nextMessage,
            ],
          }));
        }
      }, delayMs);
      interimTimers.push(timerId);
    });

    const updateBotMessage = (text: string) =>
      setStreamingMessageText(botMessageId, text);

    try {
      const response = await onSendMessage(sanitizedInputText, {
        conversationId: targetConversation.conversationId,
        externalSessionId: targetConversation.externalSessionId,
        topic: targetConversation.topic,
        onPartial: partial => {
          hasStreamedResponse = true;
          clearInterimTimers();
          const partialText = sanitizeAssistantReply(
            typeof partial === 'string' ? partial : String(partial ?? ''),
          );
          if (!partialText) {
            return;
          }
          schedulePartialUpdate(partialText);
        },
        onProgress: progress => {
          if (hasStreamedResponse) {
            return;
          }

          const progressText =
            typeof progress === 'string' ? progress.trim() : '';

          if (!progressText) {
            return;
          }

          hasBackendProgress = true;
          clearInterimTimers();
          setLoadingStatusMessagesByConversationId(previous => {
            const existingMessages = previous[targetConversation.id] || [];
            const lastMessage = existingMessages[existingMessages.length - 1];
            if (lastMessage === progressText) {
              return previous;
            }

            return {
              ...previous,
              [targetConversation.id]: [...existingMessages, progressText],
            };
          });
        },
      });

      const sanitizedReplyText = sanitizeAssistantReply(response?.replyText);
      const isApproved = resolveIsApprovedFromResponse(response);
      const fallbackReplyText = ts(
        'chat.empty',
        CHAT_TEXT.emptyResponseFallback,
      );
      const unapprovedReplyText = ts(
        'chat.unapproved',
        CHAT_TEXT.unapprovedResponseFallback,
      );
      const shouldUseUnapprovedFallback =
        isApproved !== undefined && isApproved !== true;
      const responseConversationId =
        typeof response?.conversationId === 'string' &&
        response.conversationId.trim().length > 0
          ? response.conversationId.trim()
          : null;
      const responseExternalSessionId =
        typeof response?.externalSessionId === 'string' &&
        response.externalSessionId.trim().length > 0
          ? response.externalSessionId.trim()
          : null;
      const responseMessageIndex = resolveMessageIndexFromResponse(response);
      const nextConversationId =
        responseConversationId ??
        responseExternalSessionId ??
        targetConversation.conversationId ??
        targetConversation.externalSessionId;
      const nextExternalSessionId =
        responseExternalSessionId ?? targetConversation.externalSessionId;
      const streamedReplyText = sanitizeAssistantReply(latestPartialText);

      clearPartialUpdateTimeout();
      flushPartialUpdate();

      updateConversation(
        targetConversation.id,
        conversation => ({
          ...conversation,
          conversationId: nextConversationId ?? null,
          externalSessionId: nextExternalSessionId ?? null,
          messages: conversation.messages.map(messageItem =>
            messageItem.id === botMessageId
              ? {
                  ...messageItem,
                  text: shouldUseUnapprovedFallback
                    ? unapprovedReplyText
                    : sanitizedReplyText ||
                      streamedReplyText ||
                      sanitizeAssistantReply(messageItem.text) ||
                      fallbackReplyText,
                  timestamp: new Date().toISOString(),
                  messageIndex:
                    responseMessageIndex !== undefined
                      ? responseMessageIndex ?? undefined
                      : messageItem.messageIndex,
                  isStreaming: false,
                }
              : messageItem,
          ),
          suggestions: Array.isArray(response?.suggestions)
            ? conversation.hasUsedSuggestion
              ? []
              : response.suggestions
            : conversation.suggestions,
          updatedAt: new Date().toISOString(),
        }),
        true,
      );
      refreshLastSynced({
        lastSuccessfulRunTime: response?.lastSuccessfulRunTime ?? null,
      });
    } catch (error) {
      const fallbackText = ts('chat.error', CHAT_TEXT.errorResponseFallback);
      const errorText =
        error instanceof Error && error.message ? error.message : fallbackText;
      updateConversation(targetConversation.id, conversation => ({
        ...conversation,
        messages: conversation.messages.map(messageItem =>
          messageItem.id === botMessageId
            ? {...messageItem, text: errorText, isStreaming: false}
            : messageItem,
        ),
        updatedAt: new Date().toISOString(),
      }));
    } finally {
      clearPartialUpdateTimeout();
      clearInterimTimers();
      clearStreamingMessageText(botMessageId);
      setLoadingByConversationId(previous => {
        if (!previous[targetConversation.id]) {
          return previous;
        }

        const nextLoadingByConversationId = {...previous};
        delete nextLoadingByConversationId[targetConversation.id];
        return nextLoadingByConversationId;
      });
      setLoadingStatusMessagesByConversationId(previous => {
        if (!previous[targetConversation.id]) {
          return previous;
        }

        const nextLoadingStatusMessagesByConversationId = {...previous};
        delete nextLoadingStatusMessagesByConversationId[targetConversation.id];
        return nextLoadingStatusMessagesByConversationId;
      });
    }

    return true;
  };

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isHistoryVisible) {
      return;
    }

    if (
      messages.length === 0 &&
      !isActiveConversationLoading &&
      !isActiveConversationFetchingHistory
    ) {
      clearPendingScrollTimeouts();
      scrollToTop(false);
      return;
    }

    scheduleScrollToBottom();
  }, [
    clearPendingScrollTimeouts,
    isActiveConversationFetchingHistory,
    isActiveConversationLoading,
    isHistoryVisible,
    messages,
    scheduleScrollToBottom,
    scrollToTop,
    visible,
  ]);

  useEffect(() => {
    const keyboardShowEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const keyboardHideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSubscription = Keyboard.addListener(keyboardShowEvent, () => {
      setIsKeyboardVisible(true);
      if (!visible || isHistoryVisible) {
        return;
      }

      scheduleScrollToBottom();
    });
    const hideSubscription = Keyboard.addListener(keyboardHideEvent, () => {
      setIsKeyboardVisible(false);
    });

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [isHistoryVisible, visible, scheduleScrollToBottom]);

  useEffect(
    () => () => {
      clearPendingScrollTimeouts();
    },
    [clearPendingScrollTimeouts],
  );

  useEffect(() => {
    if (!visible) {
      setIsChatInfoVisible(false);
      setIsKeyboardVisible(false);
    }
  }, [visible]);

  const handleStartNewConversation = useCallback(() => {
    if (activeConversation && isDraftConversation(activeConversation)) {
      updateConversation(activeConversation.id, conversation => ({
        ...conversation,
        title: CHAT_TEXT.newChat,
        topic: DEFAULT_CHAT_TOPIC,
        messages: [],
        suggestions: [],
        hasUsedSuggestion: false,
        updatedAt: new Date().toISOString(),
      }));
      setIsHistoryVisible(false);
      clearInput();
      return;
    }

    const existingDraftConversation = conversations.find(conversation =>
      isDraftConversation(conversation),
    );

    if (existingDraftConversation) {
      updateConversation(existingDraftConversation.id, conversation => ({
        ...conversation,
        title: CHAT_TEXT.newChat,
        topic: DEFAULT_CHAT_TOPIC,
        messages: [],
        suggestions: [],
        hasUsedSuggestion: false,
        updatedAt: new Date().toISOString(),
      }));
      setConversations(previous => {
        const draftIndex = previous.findIndex(
          conversation => conversation.id === existingDraftConversation.id,
        );
        if (draftIndex <= 0) {
          return previous;
        }

        const next = [...previous];
        const draftConversation = next[draftIndex];
        if (!draftConversation) {
          return previous;
        }
        next.splice(draftIndex, 1);
        next.unshift(draftConversation);
        return next;
      });
      setActiveConversationId(existingDraftConversation.id);
      setIsHistoryVisible(false);
      clearInput();
      return;
    }

    const nextConversation = createEmptyConversation(DEFAULT_CHAT_TOPIC);
    setConversations(prev => [nextConversation, ...prev]);
    setActiveConversationId(nextConversation.id);
    setIsHistoryVisible(false);
    clearInput();
  }, [activeConversation, clearInput, conversations, updateConversation]);

  useEffect(() => {
    if (!visible) {
      wasVisibleRef.current = false;
      hasInitializedOpenStateRef.current = false;
      return;
    }

    if (!wasVisibleRef.current) {
      wasVisibleRef.current = true;
      hasInitializedOpenStateRef.current = false;
    }

    if (
      hasInitializedOpenStateRef.current ||
      !isHistoryInitializedRef.current
    ) {
      return;
    }

    hasInitializedOpenStateRef.current = true;
    handleStartNewConversation();
  }, [visible, handleStartNewConversation]);

  const handleSelectConversation = async (conversationId: string) => {
    setActiveConversationId(conversationId);
    setIsHistoryVisible(false);

    const conversation = conversations.find(
      (conversationThread: ConversationThread) =>
        conversationThread.id === conversationId,
    );
    const deploymentConversationIdCandidates =
      getDeploymentConversationIdCandidates(conversation);
    if (deploymentConversationIdCandidates.length === 0) {
      return;
    }

    setFetchingHistoryByConversationId(
      (previousFetchingHistoryByConversationId: Record<string, boolean>) => ({
        ...previousFetchingHistoryByConversationId,
        [conversationId]: true,
      }),
    );

    try {
      let historyApiMessages: any[] = [];
      let resolvedDeploymentConversationId: string | null =
        deploymentConversationIdCandidates[0] ?? null;

      for (const candidateId of deploymentConversationIdCandidates) {
        const candidateHistoryMessages =
          await fetchDeploymentConversationHistory(candidateId);
        if (candidateHistoryMessages.length > 0) {
          historyApiMessages = candidateHistoryMessages;
          resolvedDeploymentConversationId = candidateId;
          break;
        }
      }

      if (historyApiMessages.length === 0) {
        return;
      }

      const historyMessages: Message[] = [...historyApiMessages]
        .sort((left, right) => {
          const leftIndex =
            typeof left?.messageIndex === 'number' ? left.messageIndex : null;
          const rightIndex =
            typeof right?.messageIndex === 'number' ? right.messageIndex : null;

          if (leftIndex === null && rightIndex === null) {
            return 0;
          }
          if (leftIndex === null) {
            return 1;
          }
          if (rightIndex === null) {
            return -1;
          }

          return leftIndex - rightIndex;
        })
        .map((rawMessage, index) => {
          const historyMessage = (rawMessage || {}) as HistoryApiMessage;
          const text = stripSensitiveChatText(
            resolveHistoryMessageText(historyMessage),
          );
          const role = resolveHistoryMessageRole(historyMessage);

          if (!text.trim()) {
            return null;
          }

          return {
            id: `history-${conversationId}-${
              historyMessage.messageIndex ?? index
            }`,
            text,
            isUser: isUserHistoryRole(role),
            timestamp: normalizeHistoryTimestamp(
              historyMessage.timestamp ??
                historyMessage.createdAt ??
                historyMessage.updatedAt,
            ),
            messageIndex:
              typeof historyMessage.messageIndex === 'number'
                ? historyMessage.messageIndex
                : undefined,
          };
        })
        .filter((message): message is Message => Boolean(message));

      updateConversation(conversationId, conversationThread => ({
        ...conversationThread,
        conversationId:
          resolvedDeploymentConversationId ?? conversationThread.conversationId,
        messages: historyMessages,
      }));
    } catch (_error) {
      // keep locally cached messages on API failure
    } finally {
      setFetchingHistoryByConversationId(
        (previousFetchingHistoryByConversationId: Record<string, boolean>) => {
          const nextFetchingHistoryByConversationId = {
            ...previousFetchingHistoryByConversationId,
          };
          delete nextFetchingHistoryByConversationId[conversationId];
          return nextFetchingHistoryByConversationId;
        },
      );
    }
  };

  const handleHistoryPress = () => {
    setIsHistoryVisible(previous => {
      const nextVisible = !previous;
      if (nextVisible && hasHistoryConversation) {
        setIsHistoryInitializing(true);
        if (historyOpenShimmerTimeoutRef.current) {
          clearTimeout(historyOpenShimmerTimeoutRef.current);
        }
        historyOpenShimmerTimeoutRef.current = setTimeout(() => {
          setIsHistoryInitializing(false);
          historyOpenShimmerTimeoutRef.current = null;
        }, HISTORY_OPEN_SHIMMER_MS);
      }
      return nextVisible;
    });
  };

  const handleInputFocus = () => {
    scheduleScrollToBottom();
  };

  const handleCancelEditConversationTitle = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleSaveConversationTitle = (conversationId: string) => {
    const nextTitle = editingTitle.trim();
    if (!nextTitle) {
      return;
    }

    updateConversation(conversationId, conversation => ({
      ...conversation,
      title: nextTitle,
      updatedAt: new Date().toISOString(),
    }));

    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleDeleteConversation = (conversationId: string) => {
    Alert.alert(
      ts('chat.deleteThreadTitle', CHAT_TEXT.deleteThreadTitle),
      ts('chat.deleteThreadMessage', CHAT_TEXT.deleteThreadMessage),
      [
        {
          text: ts('chat.cancel', CHAT_TEXT.cancel),
          style: 'cancel',
        },
        {
          text: ts('chat.delete', CHAT_TEXT.delete),
          style: 'destructive',
          onPress: () => {
            setConversations(previous => {
              const filtered = previous.filter(
                conversation => conversation.id !== conversationId,
              );

              if (filtered.length === 0) {
                const fallbackConversation =
                  createEmptyConversation(DEFAULT_CHAT_TOPIC);
                setActiveConversationId(fallbackConversation.id);
                return [fallbackConversation];
              }

              if (activeConversationId === conversationId) {
                const nextActiveConversationId = filtered[0]?.id;
                if (nextActiveConversationId) {
                  setActiveConversationId(nextActiveConversationId);
                }
              }

              return filtered;
            });

            if (editingConversationId === conversationId) {
              setEditingConversationId(null);
              setEditingTitle('');
            }
          },
        },
      ],
    );
  };

  const handleCopyConversationReference = (
    conversation: ConversationThread | null | undefined,
  ) => {
    if (!conversation) {
      return;
    }

    const supportReference = buildSupportReference(conversation);

    if (!supportReference) {
      Alert.alert(
        ts(
          'chat.supportReferenceUnavailableTitle',
          CHAT_TEXT.supportReferenceTitle,
        ),
        ts(
          'chat.supportReferenceUnavailableMessage',
          CHAT_TEXT.supportReferenceUnavailableMessage,
        ),
      );
      return;
    }

    copyToClipboard(supportReference);
  };

  const handleFeedback = useCallback(
    async (messageId: string, feedback: 'up' | 'down') => {
      if (!activeConversation) {
        return;
      }

      const targetMessage = activeConversation.messages.find(
        messageItem => messageItem.id === messageId,
      );
      if (!targetMessage) {
        return;
      }
      const nextFeedback =
        targetMessage?.feedback === feedback ? null : feedback;

      updateConversation(activeConversation.id, conversation => ({
        ...conversation,
        messages: conversation.messages.map(messageItem =>
          messageItem.id === messageId
            ? {...messageItem, feedback: nextFeedback}
            : messageItem,
        ),
      }));

      const deploymentConversationId =
        getDeploymentConversationIdCandidates(activeConversation)[0] || null;
      const messageIndex =
        typeof targetMessage.messageIndex === 'number'
          ? targetMessage.messageIndex
          : null;
      if (nextFeedback && deploymentConversationId) {
        const tryResolveMessageIndexFromHistory = async (): Promise<{
          deploymentConversationId: string;
          messageIndex: number;
        } | null> => {
          const deploymentConversationIdCandidates =
            getDeploymentConversationIdCandidates(activeConversation);
          const targetTextNormalized = normalizeFeedbackComparableText(
            targetMessage.text,
          );

          for (const candidateId of deploymentConversationIdCandidates) {
            try {
              const historyMessages = await fetchDeploymentConversationHistory(
                candidateId,
              );
              if (!Array.isArray(historyMessages) || !historyMessages.length) {
                continue;
              }

              const candidateMessageIndexes = historyMessages
                .map(rawMessage => {
                  const historyMessage = (rawMessage ||
                    {}) as HistoryApiMessage;
                  const historyMessageIndex =
                    typeof historyMessage.messageIndex === 'number'
                      ? historyMessage.messageIndex
                      : null;
                  const historyRole = resolveHistoryMessageRole(historyMessage);
                  if (
                    historyMessageIndex === null ||
                    isUserHistoryRole(historyRole)
                  ) {
                    return null;
                  }

                  const historyTextNormalized = normalizeFeedbackComparableText(
                    resolveHistoryMessageText(historyMessage),
                  );

                  return {
                    messageIndex: historyMessageIndex,
                    text: historyTextNormalized,
                  };
                })
                .filter(
                  (value): value is {messageIndex: number; text: string} =>
                    Boolean(value),
                );

              const matched =
                candidateMessageIndexes
                  .filter(
                    value =>
                      value.text.length > 0 &&
                      value.text === targetTextNormalized,
                  )
                  .sort(
                    (left, right) => right.messageIndex - left.messageIndex,
                  )[0] ?? null;

              if (!matched) {
                continue;
              }

              return {
                deploymentConversationId: candidateId,
                messageIndex: matched.messageIndex,
              };
            } catch (_retryError) {
              // Try next candidate ID when available
            }
          }

          return null;
        };

        try {
          if (messageIndex !== null) {
            await setDeploymentConversationFeedback(
              deploymentConversationId,
              messageIndex,
              nextFeedback === 'up' ? 'like' : 'dislike',
            );
          } else {
            const resolvedFeedbackTarget =
              await tryResolveMessageIndexFromHistory();
            if (!resolvedFeedbackTarget) {
              throw new Error('Unable to resolve message index for feedback.');
            }

            await setDeploymentConversationFeedback(
              resolvedFeedbackTarget.deploymentConversationId,
              resolvedFeedbackTarget.messageIndex,
              nextFeedback === 'up' ? 'like' : 'dislike',
            );

            updateConversation(activeConversation.id, conversation => ({
              ...conversation,
              conversationId: resolvedFeedbackTarget.deploymentConversationId,
              messages: conversation.messages.map(messageItem =>
                messageItem.id === messageId
                  ? {
                      ...messageItem,
                      messageIndex: resolvedFeedbackTarget.messageIndex,
                    }
                  : messageItem,
              ),
            }));
          }
        } catch (error) {
          const shouldRetryWithResolvedIndex =
            error instanceof Error &&
            error.message
              .toLowerCase()
              .includes('invalid message index for conversation feedback');

          if (shouldRetryWithResolvedIndex) {
            const resolvedFeedbackTarget =
              await tryResolveMessageIndexFromHistory();
            if (resolvedFeedbackTarget) {
              await setDeploymentConversationFeedback(
                resolvedFeedbackTarget.deploymentConversationId,
                resolvedFeedbackTarget.messageIndex,
                nextFeedback === 'up' ? 'like' : 'dislike',
              );

              updateConversation(activeConversation.id, conversation => ({
                ...conversation,
                conversationId: resolvedFeedbackTarget.deploymentConversationId,
                messages: conversation.messages.map(messageItem =>
                  messageItem.id === messageId
                    ? {
                        ...messageItem,
                        messageIndex: resolvedFeedbackTarget.messageIndex,
                      }
                    : messageItem,
                ),
              }));

              return;
            }
          }

          Logger.error(error);
          updateConversation(activeConversation.id, conversation => ({
            ...conversation,
            messages: conversation.messages.map(messageItem =>
              messageItem.id === messageId
                ? {...messageItem, feedback: targetMessage.feedback ?? null}
                : messageItem,
            ),
          }));
        }
      }

      Logger.manualSplunkLog({
        message:
          nextFeedback === null
            ? `Chat response feedback removed: "${targetMessage?.text ?? ''}"`
            : `Chat response rated as ${
                nextFeedback === 'up' ? 'liked' : 'not liked'
              }: "${targetMessage?.text ?? ''}"`,
        severity: 'LOW',
        extraData: {
          messageId,
          feedback: nextFeedback,
          conversationId: activeConversation.conversationId,
          externalSessionId: activeConversation.externalSessionId,
        },
      });
    },
    [activeConversation, updateConversation],
  );

  const handleDeleteAllConversations = () => {
    Alert.alert(
      ts('chat.deleteAllThreadsTitle', CHAT_TEXT.deleteAllThreadsTitle),
      ts('chat.deleteAllThreadsMessage', CHAT_TEXT.deleteAllThreadsMessage),
      [
        {
          text: ts('chat.cancel', CHAT_TEXT.cancel),
          style: 'cancel',
        },
        {
          text: ts('chat.deleteAll', CHAT_TEXT.deleteAll),
          style: 'destructive',
          onPress: () => {
            const fallbackConversation =
              createEmptyConversation(DEFAULT_CHAT_TOPIC);
            if (pendingHistoryLoadTimeoutRef.current) {
              clearTimeout(pendingHistoryLoadTimeoutRef.current);
              pendingHistoryLoadTimeoutRef.current = null;
            }
            setConversations([fallbackConversation]);
            setUnloadedHistoryCount(0);
            setActiveConversationId(fallbackConversation.id);
            setIsHistoryLoadingMore(false);

            if (editingConversationId) {
              setEditingConversationId(null);
              setEditingTitle('');
            }
          },
        },
      ],
    );
  };

  const handleLoadMoreHistory = useCallback(() => {
    if (isHistoryLoadingMore || !hasMoreHistory || !currentUserId) {
      return;
    }

    setIsHistoryLoadingMore(true);
    const loadNextHistoryPage = async () => {
      const loadStartedAt = Date.now();
      try {
        const storageKey = createChatHistoryStorageKey(currentUserId);
        const rawHistory = await AsyncStorage.getItem(storageKey);
        if (!rawHistory) {
          setUnloadedHistoryCount(0);
          return;
        }

        const parsedHistory = sanitizePersistedHistory(JSON.parse(rawHistory));
        if (!parsedHistory) {
          setUnloadedHistoryCount(0);
          return;
        }

        const historyOnlyConversations = parsedHistory.conversations.filter(
          conversation => !isDraftConversation(conversation),
        );
        const loadedHistoryCount = conversations.filter(
          conversation => !isDraftConversation(conversation),
        ).length;
        const nextPage = historyOnlyConversations.slice(
          loadedHistoryCount,
          loadedHistoryCount + HISTORY_PAGE_SIZE,
        );
        const loadedCountAfterAppend = loadedHistoryCount + nextPage.length;
        const nextUnloadedHistoryCount = Math.max(
          historyOnlyConversations.length - loadedCountAfterAppend,
          0,
        );

        setUnloadedHistoryCount(nextUnloadedHistoryCount);

        if (nextPage.length > 0) {
          setConversations(previousConversations => {
            const existingConversationIds = new Set(
              previousConversations.map(conversation => conversation.id),
            );
            const appendableConversations = nextPage.filter(
              conversation => !existingConversationIds.has(conversation.id),
            );
            if (appendableConversations.length === 0) {
              return previousConversations;
            }

            return [...previousConversations, ...appendableConversations];
          });
        }
      } catch (_error) {
        setUnloadedHistoryCount(0);
      } finally {
        const elapsedMs = Date.now() - loadStartedAt;
        const remainingShimmerMs = Math.max(
          HISTORY_LOAD_MORE_DELAY_MS - elapsedMs,
          0,
        );
        if (remainingShimmerMs > 0) {
          await new Promise(resolve => {
            pendingHistoryLoadTimeoutRef.current = setTimeout(
              resolve,
              remainingShimmerMs,
            );
          });
        }
        setIsHistoryLoadingMore(false);
        pendingHistoryLoadTimeoutRef.current = null;
      }
    };

    loadNextHistoryPage();
  }, [conversations, currentUserId, hasMoreHistory, isHistoryLoadingMore]);

  // On Android, use system inset when keyboard is closed, and a small stable
  // inset while keyboard is visible to avoid large footer jumps.
  const androidSystemBottomInset = Math.min(Math.max(insets.bottom, 8), 20);
  const androidBottomInset = isKeyboardVisible ? 8 : androidSystemBottomInset;
  const androidDisclaimerBottomInset = 2;
  const bottomInset =
    Platform.OS === 'ios' ? Math.max(insets.bottom, 12) : androidBottomInset;
  // Android: keep disclaimer inset fixed so its bottom position does not shift.
  const disclaimerBottomInset =
    Platform.OS === 'ios'
      ? Math.max(bottomInset - 8, 0)
      : androidDisclaimerBottomInset;
  const headerTopInset =
    Platform.OS === 'ios' ? 58 : Math.max(insets.top + 12, 28);
  const keyboardAvoidingBehavior = 'padding';
  const keyboardVerticalOffset = 0;
  const dynamicStyles = useMemo(
    () =>
      createDynamicStyles({
        backgroundColor,
        textColor,
        primaryColor,
        bottomInset,
        disclaimerBottomInset,
        headerTopInset,
      }),
    [
      backgroundColor,
      textColor,
      primaryColor,
      bottomInset,
      disclaimerBottomInset,
      headerTopInset,
    ],
  );

  useEffect(() => {
    const activeMessageIds = new Set(messages.map(message => message.id));
    Object.keys(parsedMessageCacheRef.current).forEach(messageId => {
      if (!activeMessageIds.has(messageId)) {
        delete parsedMessageCacheRef.current[messageId];
      }
    });
  }, [messages]);

  const getParsedMessageData = useCallback(
    (message: Message, renderedText: string) => {
      const cached = parsedMessageCacheRef.current[message.id];
      if (
        cached &&
        cached.text === renderedText &&
        cached.timestamp === message.timestamp
      ) {
        return cached;
      }

      const sanitizedText = stripSensitiveChatText(renderedText);
      const parsedData = {
        text: renderedText,
        timestamp: message.timestamp,
        formattedTime: formatMessageTimestamp(message.timestamp),
        sanitizedText,
        messageBlocks: parseChatMessageContent(sanitizedText),
      };

      parsedMessageCacheRef.current[message.id] = parsedData;
      return parsedData;
    },
    [],
  );

  const renderMessage = useCallback(
    (message: Message) => {
      const streamingText =
        message.isStreaming && streamingTextByMessageId[message.id]
          ? streamingTextByMessageId[message.id]
          : '';
      const renderedText = streamingText || message.text;
      const {formattedTime, sanitizedText, messageBlocks} =
        getParsedMessageData(message, renderedText);
      const isUser = message.isUser;
      const hasStructuredBlock = messageBlocks.some(
        block =>
          block.type === 'table' ||
          block.type === 'unordered-list' ||
          block.type === 'ordered-list',
      );
      const contentTextStyle = isUser
        ? dynamicStyles.userMessageText
        : dynamicStyles.botMessageText;

      const renderMessageBlock = (block: ChatMessageBlock, index: number) => {
        if (block.type === 'paragraph') {
          return (
            <Text
              key={`paragraph-${index}`}
              style={[styles.messageParagraph, contentTextStyle]}>
              {renderInlineText(block.text, `paragraph-${index}`)}
            </Text>
          );
        }

        if (block.type === 'unordered-list' || block.type === 'ordered-list') {
          return (
            <View key={`list-${index}`} style={styles.listBlock}>
              {block.items.map((item, itemIndex) => (
                <View
                  key={`${block.type}-${itemIndex}`}
                  style={styles.listItemRow}>
                  <Text style={[styles.listMarker, contentTextStyle]}>
                    {block.type === 'ordered-list' ? `${itemIndex + 1}.` : '•'}
                  </Text>
                  <Text style={[styles.listItemText, contentTextStyle]}>
                    {renderInlineText(item, `list-${index}-${itemIndex}`)}
                  </Text>
                </View>
              ))}
            </View>
          );
        }

        if (block.type !== 'table') {
          return null;
        }

        const columnWidths = getTableColumnWidths(block.headers, block.rows);
        const tableColumnWidthStyles = block.headers.map((_, columnIndex) => ({
          width: columnWidths[columnIndex] ?? TABLE_MIN_COLUMN_WIDTH,
        }));

        return (
          <FlatList
            key={`table-${index}`}
            data={[0]}
            renderItem={() => (
              <View
                style={[styles.tableContainer, dynamicStyles.tableContainer]}>
                <View
                  style={[
                    styles.tableRow,
                    styles.tableHeaderRow,
                    dynamicStyles.tableHeaderRow,
                  ]}>
                  {block.headers.map((header, headerIndex) => (
                    <View
                      key={`header-${headerIndex}`}
                      style={[
                        styles.tableCell,
                        tableColumnWidthStyles[headerIndex],
                        styles.tableHeaderCell,
                        headerIndex < block.headers.length - 1
                          ? styles.tableCellBorder
                          : null,
                        headerIndex < block.headers.length - 1
                          ? dynamicStyles.tableCellBorder
                          : null,
                      ]}>
                      <Text
                        style={[
                          styles.tableHeaderText,
                          dynamicStyles.tableHeaderText,
                        ]}>
                        {renderInlineText(
                          header,
                          `table-header-${index}-${headerIndex}`,
                        )}
                      </Text>
                    </View>
                  ))}
                </View>
                {block.rows.map((row, rowIndex) => (
                  <View
                    key={`row-${rowIndex}`}
                    style={[
                      styles.tableRow,
                      styles.tableBodyRow,
                      dynamicStyles.tableBodyRow,
                    ]}>
                    {block.headers.map((_, cellIndex: number) => (
                      <View
                        key={`cell-${rowIndex}-${cellIndex}`}
                        style={[
                          styles.tableCell,
                          tableColumnWidthStyles[cellIndex],
                          cellIndex < block.headers.length - 1
                            ? styles.tableCellBorder
                            : null,
                          cellIndex < block.headers.length - 1
                            ? dynamicStyles.tableCellBorder
                            : null,
                        ]}>
                        <Text
                          style={[
                            styles.tableCellText,
                            dynamicStyles.tableCellText,
                          ]}>
                          {renderInlineText(
                            row[cellIndex] || '—',
                            `table-cell-${index}-${rowIndex}-${cellIndex}`,
                          )}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            )}
            horizontal
            showsHorizontalScrollIndicator={false}
            keyExtractor={item => `table-item-${index}-${item}`}
            contentContainerStyle={styles.tableScrollView}
          />
        );
      };

      return (
        <View
          style={[
            styles.messageContainer,
            isUser ? styles.userMessage : styles.botMessage,
          ]}>
          <View
            style={[
              styles.messageRow,
              isUser ? styles.userRow : styles.botRow,
              !isUser && hasStructuredBlock ? styles.botStructuredRow : null,
            ]}>
            {isUser ? (
              <>
                <View
                  style={[
                    styles.messageBubble,
                    styles.userMessageBubble,
                    styles.userBubble,
                    dynamicStyles.userBubble,
                  ]}>
                  <View
                    style={[
                      styles.messageContent,
                      dynamicStyles.messageContent,
                    ]}>
                    <Text style={[styles.messageText, contentTextStyle]}>
                      {renderInlineText(
                        sanitizedText,
                        `user-message-${message.id}`,
                      )}
                    </Text>
                  </View>
                </View>
                <View style={[styles.messageFooterRow, styles.userFooterRow]}>
                  <View style={[styles.iconWrapper, styles.userIconWrapper]}>
                    <Avatar
                      size={28}
                      isCoach
                      avatarUrl={userAvatarUrl ?? undefined}
                      avatarKey={
                        currentUserId != null
                          ? String(currentUserId)
                          : undefined
                      }
                      fullName={userFullName ?? undefined}
                      accessible={false}
                    />
                  </View>
                </View>
              </>
            ) : (
              <>
                <View
                  style={[
                    styles.messageBubble,
                    styles.botMessageBubble,
                    hasStructuredBlock
                      ? styles.botStructuredMessageBubble
                      : null,
                    styles.botBubble,
                    dynamicStyles.botBubble,
                  ]}>
                  <View
                    style={[
                      styles.messageContent,
                      dynamicStyles.messageContent,
                    ]}>
                    {messageBlocks.length > 0 ? (
                      messageBlocks.map(renderMessageBlock)
                    ) : (
                      <Text style={[styles.messageText, contentTextStyle]}>
                        {renderInlineText(
                          sanitizedText,
                          `bot-message-${message.id}`,
                        )}
                      </Text>
                    )}
                  </View>
                </View>
                <View style={[styles.messageFooterRow, styles.botFooterRow]}>
                  <View style={[styles.iconWrapper, styles.botIconWrapper]}>
                    <Image source={botAvatar} style={styles.botAvatar} />
                  </View>
                  {!message.isStreaming && sanitizedText.trim() ? (
                    <View style={styles.feedbackRow}>
                      <TouchableOpacity
                        onPress={() => handleFeedback(message.id, 'up')}
                        style={[
                          styles.feedbackButton,
                          message.feedback === 'up'
                            ? dynamicStyles.feedbackButtonSelected
                            : dynamicStyles.feedbackButtonDefault,
                        ]}
                        accessibilityLabel={
                          CHAT_TEXT.feedbackHelpfulAccessibilityLabel
                        }>
                        <Text style={styles.feedbackEmoji}>👍</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        onPress={() => handleFeedback(message.id, 'down')}
                        style={[
                          styles.feedbackButton,
                          message.feedback === 'down'
                            ? dynamicStyles.feedbackButtonSelected
                            : dynamicStyles.feedbackButtonDefault,
                        ]}
                        accessibilityLabel={
                          CHAT_TEXT.feedbackNotHelpfulAccessibilityLabel
                        }>
                        <Text style={styles.feedbackEmoji}>👎</Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                </View>
              </>
            )}
          </View>
          {showMessageTimestamp && formattedTime ? (
            <Text
              testID="chat-message-timestamp"
              style={[
                styles.timestamp,
                dynamicStyles.timestampText,
                message.isUser ? styles.userTimestamp : styles.botTimestamp,
              ]}>
              {formattedTime}
            </Text>
          ) : null}
        </View>
      );
    },
    [
      currentUserId,
      dynamicStyles,
      getParsedMessageData,
      handleFeedback,
      showMessageTimestamp,
      streamingTextByMessageId,
      userAvatarUrl,
      userFullName,
    ],
  );

  const messageItems = useMemo<ChatMessageListItem[]>(() => {
    const nextItems: ChatMessageListItem[] = [];

    messages.forEach((message, index) => {
      const previousMessage = messages[index - 1];
      const currentDate = parseDate(message.timestamp);
      const previousDate = parseDate(previousMessage?.timestamp || '');
      const shouldShowDateTag =
        currentDate instanceof Date &&
        (!previousDate ||
          (previousDate instanceof Date &&
            !isSameCalendarDay(currentDate, previousDate)));

      if (shouldShowDateTag) {
        nextItems.push({
          type: 'date-tag',
          id: `date-tag-${message.id}`,
          timestamp: message.timestamp,
        });
      }

      nextItems.push({
        type: 'message',
        id: message.id,
        message,
      });
    });

    return nextItems;
  }, [messages]);

  const renderMessageListItem = useCallback(
    ({item}: ListRenderItemInfo<ChatMessageListItem>) => {
      if (item.type === 'date-tag') {
        return (
          <View style={styles.dateTagContainer}>
            <Text style={[styles.dateTagText, dynamicStyles.dateTagText]}>
              {formatConversationDateTag(item.timestamp)}
            </Text>
          </View>
        );
      }

      return renderMessage(item.message);
    },
    [dynamicStyles.dateTagText, renderMessage],
  );

  const messageItemKeyExtractor = useCallback(
    (item: ChatMessageListItem) => item.id,
    [],
  );
  const handleMessagesContentSizeChange = useCallback(() => {
    if (!visible || isHistoryVisible || messages.length === 0) {
      return;
    }

    const now = Date.now();
    if (now - lastContentSizeScrollAtRef.current < 140) {
      return;
    }

    lastContentSizeScrollAtRef.current = now;
    scrollToBottom(false);
  }, [isHistoryVisible, messages.length, scrollToBottom, visible]);

  const supportReferenceLabel = activeConversation
    ? formatSupportReferenceLabel(activeConversation, ts)
    : null;
  const shouldShowHistoryRefreshMeta =
    !isHistoryVisible && Boolean(activeConversation);
  const formattedLastSyncedTime = useMemo(
    () => formatLastSyncedTimestamp(lastSyncedTime),
    [lastSyncedTime],
  );

  return (
    <>
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="fullScreen"
        statusBarTranslucent={Platform.OS === 'android'}
        onRequestClose={() => {
          if (isChatInfoVisible) {
            setIsChatInfoVisible(false);
            return;
          }
          onClose();
        }}>
        <KeyboardAvoidingView
          style={[styles.container, dynamicStyles.containerBackground]}
          behavior={keyboardAvoidingBehavior}
          keyboardVerticalOffset={keyboardVerticalOffset}>
          {/* Header */}
          <View
            style={[
              styles.header,
              dynamicStyles.headerBorder,
              dynamicStyles.headerTopInset,
            ]}>
            <View style={styles.headerMainContent}>
              <View style={styles.headerTopActionsRow}>
                <View style={styles.headerTitleMetaContainer}>
                  <View style={styles.headerTitleContainer}>
                    <View style={styles.headerTitleInfoRow}>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.headerTitle,
                          dynamicStyles.headerTitleText,
                        ]}>
                        {ts('chat.title', CHAT_TEXT.title)}
                      </Text>
                      <TouchableOpacity
                        style={styles.infoButton}
                        onPress={() => setIsChatInfoVisible(true)}
                        accessibilityLabel={ts(
                          'chat.infoAccessibilityLabel',
                          CHAT_TEXT.infoAccessibilityLabel,
                        )}>
                        <Icon
                          name="info"
                          style={[styles.infoIcon, dynamicStyles.infoIcon]}
                        />
                      </TouchableOpacity>
                    </View>
                    <Text style={[styles.betaTag, dynamicStyles.betaTag]}>
                      {ts('chat.beta', CHAT_TEXT.beta)}
                    </Text>
                  </View>
                  {shouldShowHistoryRefreshMeta ? (
                    <View style={styles.headerRefreshBelowTitleContainer}>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.headerSubtitle,
                          styles.headerRefreshText,
                          dynamicStyles.headerSubtitleText,
                        ]}>
                        {`${ts(
                          'chat.refreshDataAvailableShort',
                          CHAT_TEXT.refreshDataAvailableShort,
                        )}: ${formattedLastSyncedTime}`}
                      </Text>
                    </View>
                  ) : null}
                </View>
                <View style={styles.headerActions}>
                  <View style={styles.historyActionContainer}>
                    <TouchableOpacity
                      style={styles.headerActionButton}
                      onPress={handleHistoryPress}
                      accessibilityLabel={
                        CHAT_TEXT.toggleConversationHistoryAccessibilityLabel
                      }>
                      <Text
                        style={[
                          styles.historyButtonText,
                          dynamicStyles.historyButtonText,
                        ]}>
                        {isHistoryVisible
                          ? ts('chat.hideHistory', CHAT_TEXT.hideHistory)
                          : ts('chat.showHistory', CHAT_TEXT.showHistory)}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  <TouchableOpacity
                    style={styles.closeButton}
                    onPress={onClose}
                    accessibilityLabel={CHAT_TEXT.closeChatAccessibilityLabel}>
                    <Icon
                      name="close"
                      style={[styles.closeIcon, dynamicStyles.closeIcon]}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>

          {!isHistoryVisible && messages.length > 0 ? (
            <View
              style={[
                styles.activeConversationBar,
                dynamicStyles.activeConversationBarBorder,
              ]}>
              <View style={styles.activeConversationTextContainer}>
                <Text
                  numberOfLines={1}
                  style={[
                    styles.activeConversationTitle,
                    dynamicStyles.activeConversationTitle,
                  ]}>
                  {activeConversation?.title ||
                    ts('chat.newChat', CHAT_TEXT.newChat)}
                </Text>
                {activeConversation && supportReferenceLabel ? (
                  <View style={styles.activeConversationMetaContainer}>
                    <TouchableOpacity
                      onPress={() =>
                        handleCopyConversationReference(activeConversation)
                      }
                      style={styles.activeConversationReferenceRow}
                      accessibilityLabel={
                        CHAT_TEXT.copySupportReferenceAccessibilityLabel
                      }>
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.activeConversationSupportReference,
                          dynamicStyles.activeConversationSupportReference,
                        ]}>
                        {supportReferenceLabel}
                      </Text>
                      <Icon
                        name="copy"
                        accessible={false}
                        style={[
                          styles.activeConversationSupportReferenceIcon,
                          dynamicStyles.activeConversationSupportReferenceIcon,
                        ]}
                      />
                    </TouchableOpacity>
                  </View>
                ) : null}
              </View>
              <TouchableOpacity
                style={[
                  styles.newConversationButton,
                  dynamicStyles.newConversationButton,
                ]}
                onPress={handleStartNewConversation}
                accessibilityLabel={
                  CHAT_TEXT.startNewConversationAccessibilityLabel
                }>
                <Text style={styles.newConversationButtonText}>
                  {ts('chat.newConversation', CHAT_TEXT.newConversation)}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {isHistoryVisible ? (
            <ChatHistoryPanel
              isLoading={isHistoryInitializing}
              hasHistoryConversation={hasHistoryConversation}
              historyConversationSections={historyConversationSections}
              hasMoreHistory={hasMoreHistory}
              isLoadingMoreHistory={isHistoryLoadingMore}
              activeConversationId={activeConversationId}
              editingConversationId={editingConversationId}
              editingTitle={editingTitle}
              setEditingTitle={setEditingTitle}
              onDeleteAllConversations={handleDeleteAllConversations}
              onStartNewConversation={handleStartNewConversation}
              onSelectConversation={handleSelectConversation}
              onSaveConversationTitle={handleSaveConversationTitle}
              onCancelEditConversationTitle={handleCancelEditConversationTitle}
              onDeleteConversation={handleDeleteConversation}
              onLoadMoreHistory={handleLoadMoreHistory}
              dynamicStyles={dynamicStyles}
              ts={ts}
            />
          ) : null}

          {!isHistoryVisible ? (
            <ChatMessagesPanel
              messagesListRef={messagesListRef}
              textInputRef={textInputRef}
              messagesLength={messages.length}
              isActiveConversationFetchingHistory={
                isActiveConversationFetchingHistory
              }
              isActiveConversationLoading={isActiveConversationLoading}
              primaryColor={primaryColor}
              dynamicStyles={dynamicStyles}
              activeQuickSuggestions={activeQuickSuggestions}
              loadingStatusMessages={loadingStatusMessages}
              suggestions={suggestions}
              inputResetKey={inputResetKey}
              messageItems={messageItems}
              renderMessageItem={renderMessageListItem}
              keyExtractor={messageItemKeyExtractor}
              onMessagesContentSizeChange={handleMessagesContentSizeChange}
              onSuggestionPress={handleSuggestionPress}
              onInputFocus={handleInputFocus}
              onSendText={handleSendText}
              ts={ts}
            />
          ) : null}
        </KeyboardAvoidingView>
        <ChatInfoSheet
          visible={isChatInfoVisible}
          onClose={() => setIsChatInfoVisible(false)}
          heading={ts('chat.infoHeading', CHAT_TEXT.infoHeading)}
          body={ts('chat.infoBody', CHAT_TEXT.infoBody)}
          contentInsetStyle={dynamicStyles.infoPopoverContentInset}
        />
      </Modal>
    </>
  );
};

export default withHOC(withTheme, withTranslation(''))(ChatModal);
